import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { getLscAdminIssue, updateLscAdminIssue } from '../api';
import {
  LSC_SUPPORT_ISSUE_STATUSES,
  type LscAdminSupportIssue,
  type LscSupportIssueStatus,
} from '../types';
import LoadingState from '../components/LoadingState';
import { lscPath } from '../routing';
import {
  adminIssueStatusLabel,
  formatAdminIssueDiagnosticBundle,
  LSC_ADMIN_COPY_DIAGNOSTIC_CTA,
  LSC_ADMIN_COPY_DIAGNOSTIC_SUCCESS,
  LSC_ADMIN_ISSUE_SAVE_CTA,
  LSC_ADMIN_ISSUE_SAVE_SUCCESS,
  parseAdminIssueError,
} from '../adminIssueCopy';
import {
  sanitizeSupportIssueContext,
  supportIssueCategoryLabel,
  supportIssueSeverityLabel,
} from '../supportIssueCopy';

export function LscAdminIssueDetailPageView({
  issue,
  loading,
  error,
  saving,
  saveError,
  saveSuccess,
  status,
  assignedTo,
  adminNotes,
  publicNotes,
  onStatusChange,
  onAssignedToChange,
  onAdminNotesChange,
  onPublicNotesChange,
  onSave,
  onCopyDiagnostic,
  copySuccess,
}: {
  issue: LscAdminSupportIssue | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  saveSuccess: boolean;
  status: LscSupportIssueStatus;
  assignedTo: string;
  adminNotes: string;
  publicNotes: string;
  onStatusChange: (value: LscSupportIssueStatus) => void;
  onAssignedToChange: (value: string) => void;
  onAdminNotesChange: (value: string) => void;
  onPublicNotesChange: (value: string) => void;
  onSave: () => void;
  onCopyDiagnostic: () => void;
  copySuccess: boolean;
}) {
  const safeContext = useMemo(
    () => sanitizeSupportIssueContext(issue?.context ?? {}),
    [issue?.context]
  );

  if (loading) return <LoadingState message="Loading issue detail..." />;

  if (error) {
    return (
      <div className="space-y-3" data-testid="lsc-admin-issue-detail-error">
        <p className="text-sm text-red-300" role="alert">
          {error}
        </p>
        <Link to={lscPath('/admin/issues')} className="text-primary-300 hover:text-primary-200 text-sm">
          Back to issue queue
        </Link>
      </div>
    );
  }

  if (!issue) return null;

  return (
    <div className="space-y-6" data-testid="lsc-admin-issue-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to={lscPath('/admin/issues')}
            className="text-sm text-primary-300 hover:text-primary-200"
            data-testid="lsc-admin-issue-back"
          >
            ← Back to issue queue
          </Link>
          <h2 className="text-xl font-semibold text-white mt-2">{issue.title}</h2>
          <p className="text-sm text-secondary-400 mt-1">
            {issue.issue_id} · {supportIssueCategoryLabel(issue.category)} ·{' '}
            {supportIssueSeverityLabel(issue.severity)}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={onCopyDiagnostic}
          data-testid="lsc-admin-copy-diagnostic"
        >
          {LSC_ADMIN_COPY_DIAGNOSTIC_CTA}
        </button>
      </div>

      {copySuccess ? (
        <p className="text-sm text-emerald-300" data-testid="lsc-admin-copy-diagnostic-success">
          {LSC_ADMIN_COPY_DIAGNOSTIC_SUCCESS}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card space-y-3 text-sm">
          <h3 className="font-semibold text-white">Report details</h3>
          <p className="text-secondary-300 whitespace-pre-wrap">{issue.description || '—'}</p>
          {issue.steps_to_reproduce ? (
            <div>
              <p className="text-secondary-500 text-xs uppercase tracking-wide">Steps to reproduce</p>
              <p className="text-secondary-300 whitespace-pre-wrap">{issue.steps_to_reproduce}</p>
            </div>
          ) : null}
          {issue.expected_behavior || issue.actual_behavior ? (
            <div className="space-y-2">
              {issue.expected_behavior ? (
                <div>
                  <p className="text-secondary-500 text-xs uppercase tracking-wide">Expected</p>
                  <p className="text-secondary-300 whitespace-pre-wrap">{issue.expected_behavior}</p>
                </div>
              ) : null}
              {issue.actual_behavior ? (
                <div>
                  <p className="text-secondary-500 text-xs uppercase tracking-wide">Actual</p>
                  <p className="text-secondary-300 whitespace-pre-wrap">{issue.actual_behavior}</p>
                </div>
              ) : null}
            </div>
          ) : null}
          <dl className="grid gap-2 text-secondary-400">
            <div className="flex justify-between gap-4">
              <dt>Surface</dt>
              <dd>{issue.surface || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Reporter role</dt>
              <dd>{issue.reporter_role || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Created</dt>
              <dd>{issue.created_at || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Updated</dt>
              <dd>{issue.updated_at || '—'}</dd>
            </div>
          </dl>
        </div>

        <div className="space-y-4">
          <div className="card space-y-3 text-sm">
            <h3 className="font-semibold text-white">Safe context</h3>
            <dl className="grid gap-2 text-secondary-400">
              {Object.entries(safeContext).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-4">
                  <dt className="capitalize">{key.replace(/_/g, ' ')}</dt>
                  <dd className="text-right break-all">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </dd>
                </div>
              ))}
            </dl>
            {issue.league_id ? (
              <div className="flex flex-wrap gap-3">
                <Link
                  to={lscPath(`/admin/leagues/${issue.league_id}`)}
                  className="inline-flex text-primary-300 hover:text-primary-200"
                  data-testid="lsc-admin-league-summary-link"
                >
                  Open league health summary
                </Link>
                <Link
                  to={lscPath(`/leagues/${issue.league_id}/command-center`)}
                  className="inline-flex text-primary-300 hover:text-primary-200"
                  data-testid="lsc-admin-league-link"
                >
                  Open league command center
                </Link>
              </div>
            ) : null}
          </div>

          <form
            className="card space-y-4 text-sm"
            onSubmit={(event) => {
              event.preventDefault();
              onSave();
            }}
            data-testid="lsc-admin-issue-triage-form"
          >
            <h3 className="font-semibold text-white">Triage updates</h3>

            <label className="flex flex-col gap-1">
              <span className="text-secondary-500">Status</span>
              <select
                className="input-field"
                value={status}
                onChange={(e) => onStatusChange(e.target.value as LscSupportIssueStatus)}
                disabled={saving}
                data-testid="lsc-admin-issue-status"
              >
                {LSC_SUPPORT_ISSUE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {adminIssueStatusLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-secondary-500">Assigned to</span>
              <input
                className="input-field"
                value={assignedTo}
                onChange={(e) => onAssignedToChange(e.target.value)}
                disabled={saving}
                data-testid="lsc-admin-issue-assigned-to"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-secondary-500">Admin notes (internal)</span>
              <textarea
                className="input-field min-h-[96px]"
                value={adminNotes}
                onChange={(e) => onAdminNotesChange(e.target.value)}
                disabled={saving}
                data-testid="lsc-admin-issue-admin-notes"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-secondary-500">Public notes (customer-visible)</span>
              <textarea
                className="input-field min-h-[96px]"
                value={publicNotes}
                onChange={(e) => onPublicNotesChange(e.target.value)}
                disabled={saving}
                data-testid="lsc-admin-issue-public-notes"
              />
            </label>

            {saveError ? (
              <p className="text-sm text-red-300" role="alert" data-testid="lsc-admin-issue-save-error">
                {saveError}
              </p>
            ) : null}

            {saveSuccess ? (
              <p className="text-sm text-emerald-300" data-testid="lsc-admin-issue-save-success">
                {LSC_ADMIN_ISSUE_SAVE_SUCCESS}
              </p>
            ) : null}

            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
              data-testid="lsc-admin-issue-save"
            >
              {saving ? 'Saving…' : LSC_ADMIN_ISSUE_SAVE_CTA}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const LscAdminIssueDetailPage: React.FC = () => {
  const { issueId = '' } = useParams<{ issueId: string }>();
  const [issue, setIssue] = useState<LscAdminSupportIssue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [status, setStatus] = useState<LscSupportIssueStatus>('open');
  const [assignedTo, setAssignedTo] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [publicNotes, setPublicNotes] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getLscAdminIssue(issueId)
      .then((row) => {
        if (cancelled) return;
        setIssue(row);
        setStatus(row.status);
        setAssignedTo(row.assigned_to ?? '');
        setAdminNotes(row.admin_notes ?? '');
        setPublicNotes(row.public_notes ?? '');
      })
      .catch((err) => {
        if (!cancelled) setError(parseAdminIssueError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [issueId]);

  const handleSave = async () => {
    if (!issue) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const updated = await updateLscAdminIssue(issue.issue_id, {
        status,
        assigned_to: assignedTo,
        admin_notes: adminNotes,
        public_notes: publicNotes,
      });
      setIssue(updated);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(parseAdminIssueError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyDiagnostic = async () => {
    if (!issue) return;
    const bundle = formatAdminIssueDiagnosticBundle(issue);
    try {
      await navigator.clipboard.writeText(bundle);
      setCopySuccess(true);
    } catch {
      setCopySuccess(false);
    }
  };

  return (
    <LscAdminIssueDetailPageView
      issue={issue}
      loading={loading}
      error={error}
      saving={saving}
      saveError={saveError}
      saveSuccess={saveSuccess}
      status={status}
      assignedTo={assignedTo}
      adminNotes={adminNotes}
      publicNotes={publicNotes}
      onStatusChange={setStatus}
      onAssignedToChange={setAssignedTo}
      onAdminNotesChange={setAdminNotes}
      onPublicNotesChange={setPublicNotes}
      onSave={() => {
        void handleSave();
      }}
      onCopyDiagnostic={() => {
        void handleCopyDiagnostic();
      }}
      copySuccess={copySuccess}
    />
  );
};

export default LscAdminIssueDetailPage;
